import { create } from "zustand";
import { api, cacheInvalidate } from "@/lib/api";
import { getRole } from "@/lib/auth";
import type { FacilitySummaryEntry, IncidentRecord, IncidentsSummary } from "@/lib/api";

const FACILITIES_TTL_MS = 60_000;
const POLL_INTERVAL_MS = 60_000;

export type FacilityWithContext = FacilitySummaryEntry;

type GlobalStore = {
  facilities: FacilityWithContext[];
  loading: boolean;
  lastFetched: number | null;
  error: string;
  unacknowledgedAlertCount: number;
  borrowerCount: number;
  incidents: IncidentRecord[] | null;
  incidentsLoading: boolean;
  incidentsError: string | null;
  incidentsSummary: IncidentsSummary | null;
  activeIncidentCount: number;
  applicationNewCount: number;
  _pollTimer: ReturnType<typeof setInterval> | null;
  loadFacilities: () => Promise<void>;
  refreshFacilities: () => Promise<void>;
  loadBorrowerCount: () => Promise<void>;
  loadApplicationNewCount: () => Promise<void>;
  setApplicationNewCount: (n: number) => void;
  refreshAlerts: () => Promise<void>;
  loadIncidents: (params?: { facility_id?: string }) => Promise<void>;
  loadIncidentsSummary: (params?: { facility_id?: string }) => Promise<void>;
  refreshIncidents: (params?: { facility_id?: string }) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
};

export const useGlobalStore = create<GlobalStore>((set, get) => ({
  facilities: [],
  loading: false,
  lastFetched: null,
  error: "",
  unacknowledgedAlertCount: 0,
  borrowerCount: 0,
  incidents: null,
  incidentsLoading: false,
  incidentsError: null,
  incidentsSummary: null,
  activeIncidentCount: 0,
  applicationNewCount: 0,
  _pollTimer: null,

  loadFacilities: async () => {
    const now = Date.now();
    if (get().lastFetched && now - get().lastFetched! < FACILITIES_TTL_MS && get().facilities.length > 0) return;

    set({ loading: true, error: "" });
    try {
      const summary = await api.facilitiesSummary();
      set({ facilities: summary, lastFetched: Date.now() });
    } catch (e) {
      set({ error: String(e) });
    } finally {
      set({ loading: false });
    }
  },

  // Call after creating, activating or deleting a facility or a Deal.
  // loadFacilities() alone short-circuits on FACILITIES_TTL_MS, and this store
  // outlives navigation, so the new facility would stay invisible until the tab
  // was reloaded. `/deals` goes with it: the contract's facility list, its
  // count and its derived status all move when a facility does.
  refreshFacilities: async () => {
    cacheInvalidate("/facilities");
    cacheInvalidate("/deals");
    set({ lastFetched: null });
    await get().loadFacilities();
  },

  loadBorrowerCount: async () => {
    try {
      const borrowers = await api.listBorrowers();
      set({ borrowerCount: Array.isArray(borrowers) ? borrowers.length : 0 });
    } catch {}
  },

  loadApplicationNewCount: async () => {
    try {
      const data = await api.listApplications();
      set({ applicationNewCount: data.new_count ?? 0 });
    } catch {}
  },

  setApplicationNewCount: (n: number) => set({ applicationNewCount: n }),

  refreshAlerts: async () => {
    cacheInvalidate("/facilities/summary");
    set({ lastFetched: null });
    await get().loadFacilities();
    if (getRole() === "admin") {
      cacheInvalidate("/alerts");
      try {
        const alerts = await api.getGlobalAlerts({ acknowledged: false, limit: 500 });
        set({ unacknowledgedAlertCount: alerts.length });
      } catch {}
    }
  },

  loadIncidents: async (params) => {
    set({ incidentsLoading: true, incidentsError: null });
    try {
      const incidents = await api.getGlobalIncidents({ facility_id: params?.facility_id });
      set({ incidents });
    } catch (e) {
      // A failed fetch must not read as an empty inbox (ROADMAP #40 pattern):
      // keep any loaded data, surface a distinct error state otherwise.
      set({ incidentsError: String(e) });
    } finally {
      set({ incidentsLoading: false });
    }
  },

  loadIncidentsSummary: async (params) => {
    try {
      const summary = await api.getIncidentsSummary({ facility_id: params?.facility_id });
      set({ incidentsSummary: summary });
      if (!params?.facility_id) set({ activeIncidentCount: summary.active.total });
    } catch {}
  },

  refreshIncidents: async (params) => {
    cacheInvalidate("/incidents");
    await Promise.all([get().loadIncidents(params), get().loadIncidentsSummary(params)]);
  },

  startPolling: () => {
    if (get()._pollTimer) return;
    const tick = async () => {
      if (getRole() === "admin") {
        try {
          cacheInvalidate("/alerts");
          const alerts = await api.getGlobalAlerts({ acknowledged: false, limit: 500 });
          set({ unacknowledgedAlertCount: alerts.length });
        } catch {}
        try {
          cacheInvalidate("/incidents/summary");
          const summary = await api.getIncidentsSummary();
          set({ activeIncidentCount: summary.active.total });
        } catch {}
        await get().loadBorrowerCount();
        await get().loadApplicationNewCount();
      }
      cacheInvalidate("/facilities/summary");
      set({ lastFetched: null });
      await get().loadFacilities();
    };
    tick();
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    set({ _pollTimer: timer });
  },

  stopPolling: () => {
    const timer = get()._pollTimer;
    if (timer) {
      clearInterval(timer);
      set({ _pollTimer: null });
    }
  },
}));
